import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalContent, ModalHeader, ModalBody, Tabs, Tab } from '@heroui/react';
import MediaLibraryContainer from '@/containers/media-library/MediaLibraryContainer';
import FileUploadField from '../base/FileUploadField';
import { FileMetadata } from '@/services/apis/files';
import { AcceptedFileType } from '../base/file-upload/utils';

export interface MediaModalsProps {
  isOpen?: boolean;
  onClose?: () => void;
  onFileSelect?: (file: FileMetadata) => void;
  acceptedTypes?: AcceptedFileType[];
  maxSize?: number; // in MB
  fileType?: string; // MIME type filter (e.g., 'image/*', 'video/*', 'application/pdf'). Default: all types
}

// Helper function to convert MIME type patterns to AcceptedFileType array
const convertMimeTypeToAcceptedTypes = (mimeType: string | undefined): AcceptedFileType[] => {
  if (!mimeType) return [];

  const mimeTypeLower = mimeType.toLowerCase();
  const types: AcceptedFileType[] = [];

  if (mimeTypeLower.startsWith('image/')) types.push(AcceptedFileType.IMAGE);
  if (mimeTypeLower.startsWith('video/')) types.push(AcceptedFileType.VIDEO);
  if (mimeTypeLower.includes('pdf') || mimeTypeLower === 'application/pdf') {
    types.push(AcceptedFileType.PDF);
  }

  return types;
};

const MediaModals: React.FC<MediaModalsProps> = ({
  isOpen = false,
  onClose = () => {},
  onFileSelect,
  acceptedTypes,
  maxSize = 10,
  fileType,
}) => {
  const { t } = useTranslation('admin');
  const [selectedTab, setSelectedTab] = useState<string>('library');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  // Convert fileType (MIME type) to acceptedTypes array if acceptedTypes is not provided
  const resolvedAcceptedTypes = acceptedTypes || convertMimeTypeToAcceptedTypes(fileType);

  const handleFileSelect = (file: FileMetadata) => {
    onFileSelect?.(file);
    onClose();
    // Reset state
    setSelectedTab('library');
    setUploadedFile(null);
  };

  const handleFileUploaded = (fileMetadata: FileMetadata) => {
    handleFileSelect(fileMetadata);
  };

  const handleClose = () => {
    onClose();
    // Reset state
    setSelectedTab('library');
    setUploadedFile(null);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="5xl"
      placement="center"
      backdrop="blur"
      scrollBehavior="inside"
      classNames={{
        base: 'max-h-[90vh]',
        body: 'py-0',
      }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          {t('guides.builder.modals.attachFile.title', 'Attach File')}
        </ModalHeader>
        <ModalBody>
          <Tabs
            selectedKey={selectedTab}
            onSelectionChange={(key) => setSelectedTab(key as string)}
            aria-label="File attachment options"
          >
            <Tab key="library" title={t('guides.builder.modals.attachFile.tabs.library', 'Media Library')}>
              <div className="py-4">
                <MediaLibraryContainer onFileSelect={handleFileSelect} fileType={fileType} />
              </div>
            </Tab>
            <Tab key="upload" title={t('guides.builder.modals.attachFile.tabs.upload', 'Upload File')}>
              <div className="py-4">
                <FileUploadField
                  file={uploadedFile}
                  onChange={setUploadedFile}
                  acceptedTypes={resolvedAcceptedTypes}
                  maxSize={maxSize}
                  onFileUploaded={handleFileUploaded}
                />
              </div>
            </Tab>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default MediaModals;
